import "./App.css";
import "./gnv3.css";
import "./gnv3-overrides.css";
import appIcon from "./images/favicon.png";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from "react-router-dom";
import Home from "./pages/Home";
import DeleteAccount from "./pages/DeleteAccount";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import CoachMode from "./pages/CoachMode";
import ProUpgrade from "./pages/ProUpgrade";
import AdminDashboard from "./pages/AdminDashboard";
import Invite from "./pages/Invite";
import EmailConfirmed from "./pages/EmailConfirmed";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import LegalNotice from "./pages/LegalNotice";
import React, { useState, useEffect, useCallback } from "react";
import { isLoggedIn, getUser, logout, getCurrentSubscription, getUnreadMessageCount } from "./services/authService";
import MessagingModal from "./components/MessagingModal";

function Header() {
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [loggedIn, setLoggedIn] = useState(false);
	const [isProUser, setIsProUser] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [showMessaging, setShowMessaging] = useState(false);
	const [messagingInitialPartner, setMessagingInitialPartner] = useState(null);

	useEffect(() => {
		const onOpen = (e) => {
			const detail = e.detail || {};
			if (detail.partnerId) {
				setMessagingInitialPartner({
					partnerId: detail.partnerId,
					partnerPrenom: detail.partnerPrenom || "",
					partnerNom: detail.partnerNom || "",
					partnerPhoto: detail.partnerPhoto || null,
				});
			} else {
				setMessagingInitialPartner(null);
			}
			setShowMessaging(true);
		};
		window.addEventListener("open-messaging", onOpen);
		return () => window.removeEventListener("open-messaging", onOpen);
	}, []);

	// Rafraîchit le compteur de non-lus depuis l'API. Exposé pour être appelé
	// immédiatement après ouverture d'une conversation (sinon le badge reste
	// jusqu'au prochain poll de 30s).
	const refreshUnreadCount = useCallback(async () => {
		if (!isLoggedIn()) return;
		const result = await getUnreadMessageCount();
		if (result.success) {
			setUnreadCount(result.unreadCount || 0);
		}
	}, []);

	useEffect(() => {
		let subscriptionChecked = false;
		let unreadInterval = null;

		const fetchUnreadCount = refreshUnreadCount;

		const checkAuth = () => {
			const logged = isLoggedIn();
			setLoggedIn(logged);
			if (logged) {
				setUser(getUser());
				// Check subscription once when login is detected
				if (!subscriptionChecked) {
					subscriptionChecked = true;
					checkSubscription();
					fetchUnreadCount();
					// Poll unread count every 30s while logged in
					unreadInterval = setInterval(fetchUnreadCount, 30000);
				}
			} else {
				setUser(null);
				setIsProUser(false);
				setUnreadCount(0);
				subscriptionChecked = false;
				if (unreadInterval) {
					clearInterval(unreadInterval);
					unreadInterval = null;
				}
			}
		};

		const checkSubscription = async () => {
			if (isLoggedIn()) {
				const result = await getCurrentSubscription();
				if (result.success && result.subscription) {
					const proTierIds = [4, 6];
					setIsProUser(proTierIds.includes(result.subscription.idAbonnementType));
				} else {
					setIsProUser(false);
				}
			}
		};

		checkAuth();
		// Re-check on storage change (login/logout in other tabs)
		window.addEventListener("storage", checkAuth);
		// Re-check periodically for same-tab updates
		const interval = setInterval(checkAuth, 1000);

		return () => {
			window.removeEventListener("storage", checkAuth);
			clearInterval(interval);
			if (unreadInterval) clearInterval(unreadInterval);
		};
	}, []);

	const handleLogout = () => {
		logout();
		setLoggedIn(false);
		setUser(null);
		navigate("/");
	};

	return (
		<>
			<header>
				<Link to="/" className="header-logo-link">
					<img src={appIcon} alt="GymNote" className="header-logo-icon" />
					<span className="header-logo-text">GymNote</span>
				</Link>
				<nav className="header-actions">
					{loggedIn && user ? (
						<div className="header-user-section">
							<Link to={isProUser ? "/coach" : "/pro"} className="header-dashboard-link">
								Dashboard Web {!isProUser && <span style={{ marginLeft: "4px" }}>👑</span>}
							</Link>
							<button className="header-messaging-btn" onClick={() => setShowMessaging(true)}>
								💬
								{unreadCount > 0 && (
									<span className="header-messaging-badge">
										{unreadCount > 99 ? "99+" : unreadCount}
									</span>
								)}
							</button>
							<Link to="/dashboard" className="header-user-name" title="Mon compte">
								{user.prenom} {user.nom}
							</Link>
							{Array.isArray(user.adminRoles) && user.adminRoles.includes("admin") && (
								<Link to="/admin" className="header-admin-chip" title="Interface admin">
									Admin
								</Link>
							)}
							<button onClick={handleLogout} className="header-logout-btn">
								Déconnexion
							</button>
						</div>
					) : (
						<Link to="/login" className="header-login-link">
							Connexion
						</Link>
					)}
				</nav>
			</header>
			<MessagingModal
				visible={showMessaging}
				onClose={() => { setShowMessaging(false); setMessagingInitialPartner(null); refreshUnreadCount(); }}
				onMessagesRead={refreshUnreadCount}
				initialPartner={messagingInitialPartner}
			/>
		</>
	);
}

function App() {
	return (
		<Router>
			<div className="container">
				<Header />
				<main>
					<Routes>
						<Route path="/" element={<Home />} />
						<Route path="/delete-account" element={<DeleteAccount />} />
						<Route path="/login" element={<Login />} />
						<Route path="/dashboard" element={<Dashboard />} />
						<Route path="/coach" element={<CoachMode />} />
						<Route path="/pro" element={<ProUpgrade />} />
						<Route path="/admin" element={<AdminDashboard />} />
						<Route path="/invite" element={<Invite />} />
						<Route path="/email-confirme" element={<EmailConfirmed />} />
						<Route path="/privacy" element={<Privacy />} />
						<Route path="/terms" element={<Terms />} />
						<Route path="/legal-notice" element={<LegalNotice />} />
					</Routes>
				</main>
				<footer>
					<p>Copyright 2025 Gymnote</p>
					<Link to="/delete-account" className="footer-link">Supprimer mon compte</Link>
					<Link to="/privacy" className="footer-link">Politique de confidentialité</Link>
					<Link to="/terms" className="footer-link">CGU</Link>
					<Link to="/legal-notice" className="footer-link">Mentions légales</Link>
				</footer>
			</div>
		</Router>
	);
}

export default App;
